import { useState, useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Chip,
  Divider,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import type { MessageTemplate, ParameterSet, Dataset } from '@k-ucap/shared';
import type { AssetType } from '@k-ucap/shared';
import { useFavoriteStore } from '@/stores/useFavoriteStore.js';

interface AssetItem {
  id: string;
  assetType: AssetType;
  name: string;
  description: string;
  tags: string[];
  meta: string;
  data: MessageTemplate | ParameterSet | Dataset;
  projectId: string;
}

type AssetCategory = 'MESSAGE_TEMPLATE' | 'PARAMETER_SET' | 'DATASET';

interface AssetLibraryProps {
  templates: MessageTemplate[];
  parameterSets: ParameterSet[];
  datasets: Dataset[];
  onSelectTemplate?: (template: MessageTemplate) => void;
  onSelectParameterSet?: (parameterSet: ParameterSet) => void;
  onSelectDataset?: (dataset: Dataset) => void;
}

const categoryLabels: Record<AssetCategory, string> = {
  MESSAGE_TEMPLATE: 'Message Templates',
  PARAMETER_SET: 'Parameter Sets',
  DATASET: 'Datasets',
};

export const AssetLibrary = ({
  templates,
  parameterSets,
  datasets,
  onSelectTemplate,
  onSelectParameterSet,
  onSelectDataset,
}: AssetLibraryProps) => {
  const [category, setCategory] = useState<AssetCategory>('MESSAGE_TEMPLATE');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const items = useMemo<AssetItem[]>(() => {
    switch (category) {
      case 'MESSAGE_TEMPLATE':
        return templates.map((t) => ({
          id: t.id,
          assetType: 'MESSAGE_TEMPLATE' as const,
          name: t.name,
          description: t.description,
          tags: t.tags,
          meta: `v${t.version} / ${t.messageType}`,
          data: t,
          projectId: t.projectId,
        }));
      case 'PARAMETER_SET':
        return parameterSets.map((p) => ({
          id: p.id,
          assetType: 'PARAMETER_SET' as const,
          name: p.name,
          description: p.description,
          tags: p.tags,
          meta: `scope: ${p.scope}`,
          data: p,
          projectId: p.projectId,
        }));
      case 'DATASET':
        return datasets.map((d) => ({
          id: d.id,
          assetType: 'DATASET' as const,
          name: d.name,
          description: d.description,
          tags: d.tags,
          meta: `${d.rowCount} rows / ${d.format}`,
          data: d,
          projectId: d.projectId,
        }));
    }
  }, [category, templates, parameterSets, datasets]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((i) => i.name.toLowerCase().includes(q) || i.tags.some((t) => t.toLowerCase().includes(q)));
  }, [items, search]);

  const selectedItem = useMemo(() => {
    return filteredItems.find((i) => i.id === selectedId) ?? filteredItems[0] ?? null;
  }, [filteredItems, selectedId]);

  const { isFavorite, toggle } = useFavoriteStore();

  const renderPreview = () => {
    if (!selectedItem) {
      return (
        <Typography variant="body2" color="text.secondary">
          자산을 선택하면 상세 정보가 표시됩니다.
        </Typography>
      );
    }
    const data = selectedItem.data;
    const favorite = isFavorite(selectedItem.assetType, selectedItem.id);
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{data.name}</Typography>
          <IconButton
            onClick={() =>
              toggle({
                id: selectedItem.id,
                assetType: selectedItem.assetType,
                assetId: selectedItem.id,
                name: data.name,
                projectId: selectedItem.projectId,
                href: `/generator?assetType=${selectedItem.assetType}&assetId=${selectedItem.id}`,
              })
            }
            aria-label={favorite ? 'Remove favorite' : 'Add favorite'}
          >
            {favorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {data.description || 'No description'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {data.tags.map((tag: string) => (
            <Chip key={tag} label={tag} size="small" />
          ))}
          {data.tags.length === 0 && <Chip label="no tags" size="small" variant="outlined" />}
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(data, null, 2)}
        </Box>
        {category === 'MESSAGE_TEMPLATE' && onSelectTemplate && (
          <ListItemButton onClick={() => onSelectTemplate(data as MessageTemplate)}>
            <ListItemText primary="Generator에서 이 Template 사용" />
          </ListItemButton>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={category}
        onChange={(_e, v) => {
          setCategory(v);
          setSelectedId(null);
        }}
        variant="scrollable"
      >
        <Tab label={categoryLabels.MESSAGE_TEMPLATE} value="MESSAGE_TEMPLATE" />
        <Tab label={categoryLabels.PARAMETER_SET} value="PARAMETER_SET" />
        <Tab label={categoryLabels.DATASET} value="DATASET" />
      </Tabs>
      <Box sx={{ display: 'flex', flexGrow: 1, gap: 2, mt: 2, minHeight: 0 }}>
        <Paper sx={{ width: 360, display: 'flex', flexDirection: 'column', p: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or tag"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <List sx={{ overflow: 'auto', flexGrow: 1 }}>
            {filteredItems.map((item) => (
              <ListItemButton
                key={item.id}
                selected={selectedItem?.id === item.id}
                onClick={() => {
                  setSelectedId(item.id);
                  if (category === 'MESSAGE_TEMPLATE' && onSelectTemplate)
                    onSelectTemplate(item.data as MessageTemplate);
                  if (category === 'PARAMETER_SET' && onSelectParameterSet)
                    onSelectParameterSet(item.data as ParameterSet);
                  if (category === 'DATASET' && onSelectDataset) onSelectDataset(item.data as Dataset);
                }}
              >
                <ListItemText primary={item.name} secondary={item.meta} />
              </ListItemButton>
            ))}
            {filteredItems.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                검색 결과가 없습니다.
              </Typography>
            )}
          </List>
        </Paper>
        <Paper sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>{renderPreview()}</Paper>
      </Box>
    </Box>
  );
};
